import { BlankModal, Switch } from "@components/library";
import { BlankModalProps } from "@components/library/ModalBases/BlankModal";
import { COLORS, FONTS } from "@constants";
import { useSearchParams } from "@hooks/router";
import haloLogo from "@images/logos/halo_logo.svg";
import {
  fetchNotificationSettingsFromNotification,
  updateNotificationSetting,
} from "@requests/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SegmentEventName } from "@tsTypes/__generated__/enums";
import { track } from "@utils/appUtils";
import appsignal from "@utils/appsignal";
import styled from "styled-components";

const NOTIFICATION_SETTINGS_QUERY_KEY = "notificationSettings";

const RfpUnsubscribeModal = ({ onClose, isOpen }: BlankModalProps) => {
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  // NOTE: (overload119) nid is signed, whereas _nid is not.
  // In the past we used the signed_id until we discovered bugs related to it in some small % of cases
  // so we added the _nid fallback.
  const notifIdentifierParam = searchParams.get("nid") ?? searchParams.get("_nid") ?? "";
  const queryKey = [NOTIFICATION_SETTINGS_QUERY_KEY, notifIdentifierParam] as const;

  const { data, isLoading: isSettingsLoading } = useQuery({
    queryKey,
    queryFn: () => fetchNotificationSettingsFromNotification(notifIdentifierParam),
    enabled: Boolean(notifIdentifierParam),
  });

  const { mutateAsync: mutateSetting, isLoading: isUpdatingSettings } = useMutation({
    mutationFn: ({ userId, key, enabled }: { userId: number; key: string; enabled: boolean }) =>
      updateNotificationSetting(userId, key, enabled),
    onSuccess: (response) => {
      queryClient.setQueryData(queryKey, (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          notification_settings: response.notification_settings,
        };
      });
    },
    onError: (err, variables) => {
      appsignal.sendError(err as Error, (span) => {
        span.setAction("RfpUnsubscribeModal#handleToggle");
        span.setTags({
          notifIdentifierParam,
          enabled: String(variables.enabled),
          key: variables.key,
        });
      });
    },
  });

  const userId = data?.user_id;
  const marketingSettings =
    data?.notification_settings?.filter((setting) => setting.category === "marketing") ?? [];

  const handleToggleSetting = (setting: { key: string; enabled: boolean }) => {
    track(SegmentEventName.CLICK, {
      react_component: "RfpUnsubscribeModal",
      ui_component: "Communication Preferences Toggle",
      key: setting.key,
      enabled: !setting.enabled,
    });

    if (!userId) return;

    mutateSetting({
      userId,
      key: setting.key,
      enabled: !setting.enabled,
    });
  };

  const handleUnsubscribeFromAll = async () => {
    track(SegmentEventName.CLICK, {
      react_component: "RfpUnsubscribeModal",
      ui_component: "Unsubscribe From All Button",
    });

    if (!userId || marketingSettings.length === 0) return;

    for (const setting of marketingSettings) {
      await mutateSetting({
        userId,
        key: setting.key,
        enabled: false,
      });
    }
  };

  const isDisabled =
    marketingSettings.length === 0 || isSettingsLoading || isUpdatingSettings || !userId;

  return (
    <BlankModal onClose={onClose} isOpen={isOpen} width="960px" height="100%">
      <Container>
        <Top>
          <Logo src={haloLogo} alt="Halo home" />
          <HeadingContainer>
            <Subheader>Communication preferences</Subheader>
            <Header>Choose which Halo emails you&apos;d like to receive.</Header>
          </HeadingContainer>
          {isSettingsLoading ? (
            <PlaceholderState>Loading your preferences…</PlaceholderState>
          ) : (
            <OptionContainer>
              {marketingSettings.length === 0 ? (
                <PlaceholderState>No marketing email preferences available.</PlaceholderState>
              ) : (
                marketingSettings.map((setting) => (
                  <OptionRow key={setting.key} margin="4px 0 0">
                    <Switch
                      checked={setting.enabled}
                      onChange={() => handleToggleSetting(setting)}
                      margin="4px 0 0"
                      disabled={isDisabled}
                    />
                    <div>
                      <OptionHeading>{setting.title}</OptionHeading>
                      <OptionDescription>{setting.description}</OptionDescription>
                    </div>
                  </OptionRow>
                ))
              )}
            </OptionContainer>
          )}
        </Top>
        <Bottom>
          <StyledButton onClick={handleUnsubscribeFromAll} disabled={isDisabled}>
            Unsubscribe from all Halo communications
          </StyledButton>
        </Bottom>
      </Container>
    </BlankModal>
  );
};

const Container = styled.div`
  display: grid;
  grid-template-rows: 1fr auto;
`;

const Top = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  gap: 40px;

  padding: 48px;

  box-sizing: border-box;
`;

const Logo = styled.img`
  width: 65px;
  height: 32px;
`;

const HeadingContainer = styled.div`
  width: 100%;
`;

const Header = styled.div`
  color: ${COLORS.BLACK};
  ${FONTS.HEADING_3_REGULAR}
`;

const Subheader = styled.div`
  color: ${COLORS.NEUTRAL_500};
  ${FONTS.SEMIBOLD_1}
`;

const Bottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  padding: 24px 72px;

  border-top: 1px solid ${COLORS.NEUTRAL_300};

  ${FONTS.REGULAR_2}
  line-height: 14px;
  gap: 3px;
`;

const StyledButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  outline: inherit;

  ${FONTS.SEMIBOLD_2}

  color: ${COLORS.HALO_BLUE};

  text-decoration: underline !important;
  text-decoration-color: ${COLORS.BLUE_600} !important;
  -webkit-text-decoration-color: ${COLORS.BLUE_600} !important;
  text-underline-offset: 4px !important;

  &:hover:not(:disabled) {
    color: ${COLORS.BLACK};
    text-decoration-color: ${COLORS.BLACK} !important;
    -webkit-text-decoration-color: ${COLORS.BLACK} !important;
  }

  &:focus {
    color: ${COLORS.BLACK};
    text-decoration: none;
  }

  &:disabled {
    cursor: default;
    color: ${COLORS.NEUTRAL_400};
    text-decoration: none !important;
  }
`;

const OptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 34px;
  width: 100%;
`;

const OptionRow = styled.div<{ margin?: string }>`
  display: flex;
  gap: 11px;
  align-items: flex-start;
  width: 100%;
  ${({ margin }) => margin && `margin: ${margin}`};
`;

const OptionHeading = styled.div`
  ${FONTS.MEDIUM_1}
`;

const OptionDescription = styled.div`
  color: ${COLORS.NEUTRAL_500};
  ${FONTS.REGULAR_2}
`;

const PlaceholderState = styled.div`
  ${FONTS.REGULAR_2}
  color: ${COLORS.NEUTRAL_500};
`;

export default RfpUnsubscribeModal;
